import type { DnsRecord } from '@/api/hooks/use-domains'
import { CopyButton } from '@/components/ui/copy-button'

const PURPOSE_ORDER = ['spf', 'dkim', 'dmarc', 'mx', 'verification'] as const

function groupByPurpose(records: DnsRecord[]): Map<string, DnsRecord[]> {
  const groups = new Map<string, DnsRecord[]>()
  for (const purpose of PURPOSE_ORDER) {
    const matching = records.filter((r) => r.purpose.toLowerCase() === purpose)
    if (matching.length > 0) groups.set(purpose, matching)
  }
  const known = new Set<string>(PURPOSE_ORDER)
  const other = records.filter((r) => !known.has(r.purpose.toLowerCase()))
  if (other.length > 0) groups.set('other', other)
  return groups
}

function getDkimLabel(record: DnsRecord): string | null {
  if (record.purpose.toLowerCase() !== 'dkim') return null
  if (record.name.includes('oe1rsa._domainkey')) return 'DKIM \u2014 RSA (fallback)'
  if (record.name.includes('oe1._domainkey')) return 'DKIM \u2014 Ed25519 (primary)'
  return null
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  return `${text.slice(0, max - 3)}\u2026`
}

/** Parse MX value like "10 mail.one-email.com" into { priority, server }. */
function parseMxValue(value: string): { priority: string; server: string } | null {
  const match = value.match(/^(\d+)\s+(.+)$/)
  if (!match) return null
  return { priority: match[1] ?? '', server: match[2] ?? '' }
}

function RecordTable({
  purpose,
  records,
}: {
  purpose: string
  records: DnsRecord[]
}): React.JSX.Element {
  const isMx = purpose === 'mx'
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-text-primary mb-0">
        {purpose}
      </h4>
      <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
        <div className={`grid gap-2 bg-surface-1 px-4 py-2 ${
          isMx ? 'grid-cols-[60px_1fr_36px_70px_1fr_36px]' : 'grid-cols-[60px_1fr_36px_1fr_36px]'
        }`}>
          <span className="text-xs font-medium text-text-tertiary">Type</span>
          <span className="text-xs font-medium text-text-tertiary">Name</span>
          <span />
          {isMx && <span className="text-xs font-medium text-text-tertiary">Priority</span>}
          <span className="text-xs font-medium text-text-tertiary">Content</span>
          <span />
        </div>
        {records.map((record, idx) => {
          const dkimLabel = getDkimLabel(record)
          const isRsaFallback = record.name.includes('oe1rsa._domainkey')
          const mx = isMx ? parseMxValue(record.value) : null
          const displayValue = mx ? mx.server : record.value
          const copyValue = mx ? mx.server : record.value
          return (
            <div key={`${record.type}-${record.name}-${record.value}`}>
              {dkimLabel && (
                <div className="px-4 pt-3 pb-1">
                  <span className="text-xs font-medium text-text-secondary">{dkimLabel}</span>
                </div>
              )}
              <div
                className={`grid items-start gap-2 px-4 py-3 ${
                  isMx ? 'grid-cols-[60px_1fr_36px_70px_1fr_36px]' : 'grid-cols-[60px_1fr_36px_1fr_36px]'
                } ${idx < records.length - 1 && !isRsaFallback ? 'border-b border-surface-3' : ''}`}
              >
                <span className="inline-flex">
                  <span className="rounded bg-surface-2 px-2 py-0.5 font-mono text-xs font-medium text-text-secondary">
                    {record.type}
                  </span>
                </span>
                <span className="break-all font-mono text-xs text-text-primary" title={record.name}>
                  {truncate(record.name, 30)}
                </span>
                <CopyButton value={record.name} />
                {mx && (
                  <span className="font-mono text-xs font-semibold text-text-primary">
                    {mx.priority}
                  </span>
                )}
                <span
                  className="break-all font-mono text-xs text-text-primary"
                  title={displayValue}
                >
                  {truncate(displayValue, 50)}
                </span>
                <CopyButton value={copyValue} />
              </div>
              {isRsaFallback && (
                <div
                  className={`px-4 pb-3 ${idx < records.length - 1 ? 'border-b border-surface-3' : ''}`}
                >
                  <p className="text-xs text-text-tertiary">
                    Some older email servers don't support Ed25519. The RSA record ensures your
                    emails always pass DKIM verification.
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {isMx && (
        <p className="text-xs text-text-tertiary">
          In Cloudflare: enter Priority and Content as separate fields.
        </p>
      )}
    </div>
  )
}

export function DomainRecordTables({ records }: { records: DnsRecord[] }): React.JSX.Element {
  const grouped = groupByPurpose(records)
  return (
    <div className="flex flex-col gap-6">
      {[...grouped.entries()].map(([purpose, recs]) => (
        <div key={purpose} className="flex flex-col gap-2">
          {purpose === 'dkim' && (
            <p className="text-xs text-text-secondary">
              We sign with both Ed25519 (modern, fast) and RSA (universal fallback) to maximize
              deliverability.
            </p>
          )}
          <RecordTable purpose={purpose} records={recs} />
        </div>
      ))}
    </div>
  )
}
